import type { AdminClientContext, AdminHttpClient } from './client.js';
import type { AdminGroupView, Entitlement, UserListQuery } from '../../../shared-types/src/index.js';

type MethodKeys =
  | 'listUsers'
  | 'listGroups'
  | 'inviteUser'
  | 'createUserWithPassword'
  | 'updateUserRoles'
  | 'disableUser'
  | 'enableUser'
  | 'deleteUser'
  | 'listPlatformAdmins'
  | 'grantPlatformAdmin'
  | 'revokePlatformAdmin'
  | 'assignSeat'
  | 'createGroup'
  | 'updateGroupMembers'
  | 'updateAllowlist'
  | 'updateScopedAllowlist'
  | 'updateDownloadAccess';

export function buildUsersMethods(ctx: AdminClientContext) {
  const { transport, token, idemKey } = ctx;

  return {
    listUsers: ((query?: UserListQuery) =>
      query
        ? transport.listUsers(token(), query)
        : transport.listUsers(token())) as AdminHttpClient['listUsers'],
    listGroups: () => transport.listGroups(token()),
    listPlatformAdmins: () => transport.listPlatformAdmins(token()),
    inviteUser: (email, name, roles, idempotencyKey) =>
      transport.inviteUser(token(), {
        email,
        name,
        roles,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    createUserWithPassword: (email, name, password, roles, idempotencyKey) =>
      transport.createUserWithPassword(token(), {
        email,
        name,
        password,
        roles,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    updateUserRoles: async (userId, roles, idempotencyKey) => {
      await transport.updateUserRoles(token(), {
        userId,
        roles,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
      return { ok: true };
    },
    disableUser: async (userId, idempotencyKey) => {
      await transport.disableUser(token(), {
        userId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
      return { ok: true };
    },
    enableUser: async (userId, idempotencyKey) => {
      await transport.enableUser(token(), {
        userId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
      return { ok: true };
    },
    deleteUser: async (userId, idempotencyKey) => {
      await transport.deleteUser(token(), {
        userId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
      return { ok: true };
    },
    grantPlatformAdmin: (input, idempotencyKey) =>
      transport.grantPlatformAdmin(token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    revokePlatformAdmin: async (userId, idempotencyKey) => {
      await transport.revokePlatformAdmin(token(), {
        userId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
      return { ok: true };
    },
    assignSeat: async (userId, seatStatus, idempotencyKey) => {
      await transport.assignSeat(token(), {
        userId,
        seatStatus,
        idempotencyKey: idempotencyKey ?? idemKey(),
      });
      return { ok: true };
    },
    createGroup: (name, idempotencyKey) =>
      transport.createGroup(token(), {
        name,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    updateGroupMembers: (groupId, patch, idempotencyKey) =>
      transport
        .updateGroupMembers(token(), {
          groupId,
          ...patch,
          idempotencyKey: idempotencyKey ?? idemKey(),
        })
        .then((group) => ({
          ...group,
          memberIds: [] as string[],
        })) as Promise<AdminGroupView>,
    updateAllowlist: (
      userId,
      allowedModelIds,
      defaultModelId,
      idempotencyKey
    ) =>
      transport.updateAllowlist(token(), {
        subjectType: 'user',
        subjectId: userId,
        allowedModelIds,
        defaultModelId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    updateScopedAllowlist: (
      subjectType,
      subjectId,
      allowedModelIds,
      defaultModelId,
      idempotencyKey
    ) =>
      transport.updateAllowlist(token(), {
        subjectType,
        subjectId,
        allowedModelIds,
        defaultModelId,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    updateDownloadAccess: async (userId, patch, idempotencyKey) => {
      return (await transport.updateDownloadChannels(token(), {
        userId,
        ...patch,
        idempotencyKey: idempotencyKey ?? idemKey(),
      })) as Entitlement;
    },
  } satisfies Pick<AdminHttpClient, MethodKeys>;
}
